import React, { useEffect, useState } from 'react';
import { ArrowLeft, Ruler } from 'lucide-react';
import api from '../lib/api';
import FeatureHeader from './FeatureHeader';

export default function UnitsMetrics({ onBack }) {
  const [units, setUnits] = useState({
    weight: 'kg',
    distance: 'km',
    temperature: '°C',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    const loadProfileUnits = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/profile');
        const profileUnits = response?.data?.profile?.units;
        if (profileUnits) {
          setUnits(prev => ({
            ...prev,
            ...profileUnits,
          }));
        }
      } catch (err) {
        if (err?.response?.status !== 404) {
          setStatus('Failed to load saved preferences');
        }
      } finally {
        setLoading(false);
      }
    };

    loadProfileUnits();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setStatus('');
      await api.post('/api/profile', { units });
      setStatus('Preferences saved');
    } catch (err) {
      setStatus(err?.response?.data?.message || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      padding: '24px 16px',
      background: 'var(--bg)',
      fontFamily: 'sans-serif',
      color: 'var(--on-bg)',
    }}>
      {/* Back Button */}
      <FeatureHeader title = "Units and Preferences" onBack={onBack} />

      {/* Icon Card */}
      <div style={{
        background: 'var(--surface)',
        borderRadius: 12,
        padding: 16,
        marginBottom: 24,
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}>
        <Ruler size={24} style={{ color: 'var(--accent)' }} />
        <div>
          <div style={{ fontSize: '0.95rem', fontWeight: '600' }}>Customize your measurement units</div>
          <div style={{ fontSize: '0.75rem', color: '#666' }}>Choose what feels natural to you.</div>
        </div>
      </div>

      {/* Unit Preferences */}
      <label style={labelStyle}>Weight Unit</label>
      <select
        style={inputStyle}
        value={units.weight}
        onChange={(e) => setUnits(prev => ({ ...prev, weight: e.target.value }))}
        disabled={loading || saving}
      >
        <option value="kg">kg</option>
        <option value="lbs">lbs</option>
      </select>

      <label style={labelStyle}>Distance Unit</label>
      <select
        style={inputStyle}
        value={units.distance}
        onChange={(e) => setUnits(prev => ({ ...prev, distance: e.target.value }))}
        disabled={loading || saving}
      >
        <option value="km">km</option>
        <option value="miles">miles</option>
      </select>

      <label style={labelStyle}>Temperature Unit</label>
      <select
        style={inputStyle}
        value={units.temperature}
        onChange={(e) => setUnits(prev => ({ ...prev, temperature: e.target.value }))}
        disabled={loading || saving}
      >
        <option value="°C">°C</option>
        <option value="°F">°F</option>
      </select>

      {status ? (
        <div style={{ marginTop: 12, fontSize: '0.8rem', color: 'var(--on-bg)' }}>
          {status}
        </div>
      ) : null}

      <button
        style={{ ...buttonStyle, opacity: loading || saving ? 0.7 : 1, cursor: loading || saving ? 'not-allowed' : 'pointer' }}
        onClick={handleSave}
        disabled={loading || saving}
      >
        {saving ? 'Saving...' : 'Save Preferences'}
      </button>
    </div>
  );
}

const labelStyle = {
  fontSize: '0.85rem',
  marginTop: 16,
  marginBottom: 6,
  display: 'block',
};

const inputStyle = {
  width: '100%',
  padding: 10,
  borderRadius: 8,
  border: '1px solid #ccc',
  background: 'var(--surface)',
  color: 'var(--on-bg)',
  fontSize: '0.9rem',
};

const buttonStyle = {
  marginTop: 24,
  padding: '10px 16px',
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: '0.9rem',
  fontWeight: '500',
};